import {
    DATE_TIME_FORMAT_NAME_MODEL,
    formatMinutesToHoursMinutes,
    getDateFormat,
    getTimeFormat
} from "../../../../../Apis/functions";
import { IconScheduleTable, TimePickerChip } from "../../../../../components/buttons/IconButtons";
import CustomChip from "../../../../../components/CustomChip";
import { colors } from "../../../../../enums/colors";
import {getAttendanceColorByStatus} from "../Models/Model";
import {getAccessMethodById} from "../../../../../enums/accessMethodEnum";
import React from "react";
import CustomTooltip from "../../../../../components/CustomTooltip";
import {useTranslation} from "react-i18next";
import AccessMethodIcon from "../../../../../components/icons/AccessMethodIcon";
import {
    ASTERISK,
    ATTENDANCE_STATUS_MODEL,
    getAdjustedBoundIcon,
    TYPE_MODEL_COLOR_MODEL
} from '../../../Company/Payroll/Model/Model';

/**
 * @description
 * EntryExitOption renders a compact visual summary of entry and exit data for a person,
 * including device names, timestamps, access methods (with icons), and total time.
 * It uses color-coded chips based on status and optionally includes action buttons.
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Software Developer
 * @Email: kpozos@ibl.mx
 *
 * @param {Object} props - Component props.
 * @param {Object} props.record - Record data containing entry, exit, and total time information.
 * @param {Function} [props.getActionsButtons] - Optional function to render additional action buttons.
 * @param {string} props.color - Base color used to determine the chip and status colors.
 * @param {string} props.model - Model used to retrieve variables for setting the color.
 * @param {boolean} [props.showScheduledShift] - Renders the shift the record was expected in, before the entry column.
 *
 * @returns {JSX.Element} A component displaying entry and exit info with visual cues and optional actions.
 */

const EntryExitOption = ({ record, getActionsButtons = null, color,model, showScheduledShift = false }) => {

    const {t} = useTranslation();
    // A bound with no date reads as an empty record, so it keeps the neutral style
    // instead of taking the one of its status.
    const entryStatus = record?.entryDate ? record?.entryDateStatus : null;
    const exitStatus = record?.exitDate ? record?.exitDateStatus : null;
    const totalStatus = record?.totalTime != null ? record?.totalTimeStatus : null;

    let entryBackgroundColor = getAttendanceColorByStatus(entryStatus, color, model);
    let entryBackgroundColorTxt = getAttendanceColorByStatus(entryStatus, color, model,TYPE_MODEL_COLOR_MODEL.TXT_COLOR);
    let exitBackgroundColor = getAttendanceColorByStatus(exitStatus, color, model);
    let exitBackgroundColorTxt = getAttendanceColorByStatus(exitStatus, color, model,TYPE_MODEL_COLOR_MODEL.TXT_COLOR);
    let totalBackgroundColor = getAttendanceColorByStatus(totalStatus, color, model);
    let totalBackgroundColorTxt = getAttendanceColorByStatus(totalStatus, color, model,TYPE_MODEL_COLOR_MODEL.TXT_COLOR);

    const entryAccessMethod = getAccessMethodById(record?.entryAccessMethodId);
    const exitAccessMethod = getAccessMethodById(record?.exitAccessMethodId);

    const EntryAdjustmentIcon = getAdjustedBoundIcon(record?.entryAccessMethodId, record?.entryDateStatus);
    const ExitAdjustmentIcon = getAdjustedBoundIcon(record?.exitAccessMethodId, record?.exitDateStatus);

    /**
     * Generates a time chip component for entry/exit display.
     *
     * - Formats date and time
     * - Adds an asterisk for off-schedule statuses
     * - Falls back to "no_record" when no date is present
     *
     * @param {string} backgroundColor - Chip background color
     * @param {string} backgroundColorTxt - Chip text color
     * @param {number} status - Attendance status ID
     * @param {number|string} date - Timestamp value
     *
     * @returns {JSX.Element} CustomChip component with formatted time
     */
    const getTimeChip = ( backgroundColor, backgroundColorTxt, status, date ) => {
        const showAsterisk = [
            Number(ATTENDANCE_STATUS_MODEL.offScheduleDeparture.id),
            Number(ATTENDANCE_STATUS_MODEL.offScheduleArrival.id)
        ].includes(Number(status)) ? ASTERISK : '';

        return <CustomChip
            background={backgroundColor}
            fontColor={backgroundColorTxt}
            legend={date ? (
                <div className='d-flex flex-column text-center'>
                    <span>
                        {showAsterisk}
                        {getDateFormat(
                            date,
                            false,
                            DATE_TIME_FORMAT_NAME_MODEL.slash,
                        )}
                    </span>
                    {/* 24 hour clock, so the record lines up with the scheduled shift and the daily table. */}
                    <span>{getTimeFormat(date, DATE_TIME_FORMAT_NAME_MODEL.HHmmss)}</span>
                </div>
            ) : (
                <span>{t('no_record')}</span>
            )}
            className={`w-120px ${date ? 'h-40px' : ''}`}
        />
    }
    /**
     * Renders a scheduled bound on a single line. The row is 90px tall and already holds
     * two-line chips, so both bounds have to stay compact.
     *
     * @param {string} label - Translation key describing the bound
     * @param {number|string} date - Scheduled timestamp
     * @returns {JSX.Element} Calendar icon, label and the scheduled datetime chip
     */
    const getScheduledBound = (label, date) => (
        <div className='d-flex align-items-center fs-14px font-color-general-950 text-nowrap'>
            <IconScheduleTable className='mr-8px icon-color-general-950' />
            <span className='ibl-font-w-600 mr-8px'>{t(label)}:</span>
            <CustomChip
                background={colors.secondary100}
                borderColor={colors.secondary150}
                fontColor={colors.secondary700}
                legend={date
                    ? getDateFormat(date, null, DATE_TIME_FORMAT_NAME_MODEL.dateTimeWithSeconds)
                    : t('no_record')
                }
            />
        </div>
    );

    return (
        <div className="entry-exit-option body-content w-100">
            { showScheduledShift &&
                <>
                    <div className="record-col scheduled px-2">
                        { (record?.scheduledStart || record?.scheduledEnd)
                            ? <div className='d-flex flex-column gap-2'>
                                { getScheduledBound('from', record?.scheduledStart) }
                                { getScheduledBound('to', record?.scheduledEnd) }
                            </div>
                            : <CustomChip
                                background={getAttendanceColorByStatus(null, color, model)}
                                fontColor={getAttendanceColorByStatus(null, color, model, TYPE_MODEL_COLOR_MODEL.TXT_COLOR)}
                                legend={t('no_record')}
                                className='w-120px'
                            />
                        }
                    </div>
                    <div className="vertical-separator-shades-950 h-71px background-shades-200" />
                </>
            }
            <div className={`record-col in px-2`}>
                <div className="access-block">
                    <div className={"w-120px"}>
                        <CustomTooltip
                            text={record.entryDeviceName}
                            classes={"font-color-general-950 ibl-font-w-500 fs-14px text-ellipsis-1-row"}
                            showIfTruncated={true}
                            placement={"top"}
                        />
                    </div>
                    <div className="access-content">
                        {entryAccessMethod?.icon &&
                            <CustomTooltip text={t(entryAccessMethod.value)}>
                               <p><AccessMethodIcon fill={entryBackgroundColorTxt} AccessMethodId={record?.entryAccessMethodId}/></p>
                            </CustomTooltip>
                        }
                        {/* A bound with no date reads as an empty record, so it carries no icon. */}
                        {EntryAdjustmentIcon && record?.entryDate &&
                            <p><EntryAdjustmentIcon className='btn-icon-svg-24px' /></p>
                        }
                        <div className='d-flex flex-column align-items-center text-center gap-2'>
                            { getTimeChip(entryBackgroundColor, entryBackgroundColorTxt, record?.entryDateStatus, record.entryDate)}
                        </div>
                    </div>
                </div>
            </div>
            <div className="vertical-separator-shades-950 h-71px background-shades-200" />
            <div className="record-col out px-2">
                <div className="access-block">
                    <div className={"w-120px"}>
                        <CustomTooltip
                            text={record.exitDeviceName}
                            classes={"font-color-general-950 ibl-font-w-500 fs-14px text-ellipsis-1-row "}
                            showIfTruncated={true}
                            placement={"top"}
                        />
                    </div>

                    <div className="access-content">
                        {exitAccessMethod?.icon &&
                            <CustomTooltip text={t(exitAccessMethod.value)}>
                              <p><AccessMethodIcon fill={exitBackgroundColorTxt} AccessMethodId={record?.exitAccessMethodId}/></p>
                            </CustomTooltip>
                        }
                        {ExitAdjustmentIcon && record?.exitDate &&
                            <p><ExitAdjustmentIcon className='btn-icon-svg-24px' /></p>
                        }
                        <div className='d-flex flex-column align-items-center text-center gap-2'>
                            { getTimeChip(exitBackgroundColor, exitBackgroundColorTxt, record?.exitDateStatus, record.exitDate)}
                        </div>
                    </div>
                </div>
            </div>


            <div className="record-col center-symbol">
                =
            </div>

            <div className={`record-col duration pr-2 ${record.entryAccessMethodId ? "br-divider" : ""}`}>
                <CustomChip
                    icon={record.totalTime ? <TimePickerChip fill={totalBackgroundColorTxt} /> : undefined}
                    background={totalBackgroundColor}
                    fontColor={totalBackgroundColorTxt}
                    legend={
                        record?.totalTime != null
                            ? formatMinutesToHoursMinutes(record.totalTime)
                            : t('no_record')
                    }
                    className='w-125px'
                />
            </div>

            <div className="record-col align-content-center">
                { getActionsButtons ? <>
                    <div className="vertical-separator-shades-950 h-71px background-shades-200 m-0" />
                        {getActionsButtons()}
                </> :
                    <div className={"w-40px"}/>
                }
            </div>
        </div>
    );
};

export default EntryExitOption;
